import { format, isValid, parse } from 'date-fns';

import {
    CareMonthOverview,
    CareRow,
    CareRows,
    CodeActionDescriptor,
    CurrentReport,
    DecisionTokens,
    TerminationRecord
} from './types';

// Durations are provider-reported seconds, never decimal minutes or billing arithmetic.
export const duration = (seconds: number | null | undefined): string =>
    seconds === null || seconds === undefined
        ? '—'
        : `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

export const timeText = (seconds: number | null | undefined): string => {
    if (seconds === null || seconds === undefined) return 'Not recorded';
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return (
        [
            minutes ? `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}` : '',
            rest ? `${rest} ${rest === 1 ? 'second' : 'seconds'}` : ''
        ]
            .filter(Boolean)
            .join(' ') || '0 minutes'
    );
};

const validDate = (value: unknown): Date | null => {
    if (typeof value !== 'string' || !value) return null;
    const date = new Date(value);
    return isValid(date) ? date : null;
};

export const dateTime = (value: string | null | undefined): string =>
    validDate(value)?.toLocaleString() ?? 'Unknown';

export const csvDateTime = (value: string | null | undefined): string => {
    const date = validDate(value);
    return date ? format(date, 'yyyy-MM-dd HH:mm xxx') : 'Unknown';
};

export const csvEnrollmentDate = (value: string): string => {
    const date = validDate(value);
    return date ? format(date, 'yyyy-MM-dd') : 'Unknown';
};

// Provider work dates are calendar dates, not instants to shift into a browser timezone.
export const workDateText = (value: string): string => {
    const day = parse(value, 'yyyy-MM-dd', new Date());
    return isValid(day) ? format(day, 'MMM d, yyyy') : 'Date unavailable';
};

const recordedDate = (value: unknown): string | null => validDate(value)?.toISOString() ?? null;

export const enrollmentDate = (value: string): string =>
    validDate(value)?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) ?? 'Unknown';

export const enrollmentPeriods = (row: CareRow, terminations: TerminationRecord[] = []) => {
    const periods = new Map<string, { enrolledAt: string | null; disenrolledAt: string | null }>();
    row.eligibility.lineage.forEach((line) => {
        const enrolledAt = recordedDate(line.program_enrollment_date);
        const disenrolledAt = recordedDate(line.program_disenrollment_date);
        if (enrolledAt || disenrolledAt)
            periods.set(`${enrolledAt}:${disenrolledAt}`, { enrolledAt, disenrolledAt });
    });
    if (!periods.size) {
        terminations
            .filter((record) => record.hero_user_id === row.hero_user_id && record.program === row.program)
            .forEach((record) => {
                const enrolledAt = recordedDate(record.enrolled_at);
                const disenrolledAt =
                    record.termination_reason === 'program_disenrollment'
                        ? recordedDate(record.terminated_at)
                        : null;
                if (enrolledAt || disenrolledAt)
                    periods.set(`${enrolledAt}:${disenrolledAt}`, { enrolledAt, disenrolledAt });
            });
    }
    return Array.from(periods.values()).sort((a, b) =>
        (a.enrolledAt ?? a.disenrolledAt ?? '').localeCompare(b.enrolledAt ?? b.disenrolledAt ?? '')
    );
};

export const patientEnrollmentEndings = (
    rows: CareRow[],
    month: string,
    terminations: TerminationRecord[],
    formatDate = enrollmentDate
): string[] => {
    const endings = new Set<string>();
    const add = (label: string, value: unknown) => {
        const date = recordedDate(value);
        // The service month is UTC; display the saved timestamp in the reviewer's local zone.
        if (date?.startsWith(`${month}-`)) endings.add(`${label}: ${formatDate(date)}`);
    };
    rows.forEach((row) =>
        row.eligibility.lineage.forEach((line) => {
            add('Membership cancelled', line.member_cancelled_at);
            add('Membership discharged', line.member_discharged_at);
        })
    );
    terminations
        .filter((record) => record.hero_user_id === rows[0]?.hero_user_id)
        .forEach((record) => {
            if (record.termination_reason === 'membership_cancellation')
                add('Membership cancelled', record.terminated_at);
            if (record.termination_reason === 'membership_discharge')
                add('Membership discharged', record.terminated_at);
            if (
                record.termination_reason === 'program_disenrollment' &&
                !rows.some((row) => row.program === record.program)
            )
                add(`${record.program} disenrolled`, record.terminated_at);
        });
    return Array.from(endings);
};

export const patientCancellationDates = (
    rows: CareRow[],
    month: string,
    terminations: TerminationRecord[],
    userId: number,
    formatDate = enrollmentDate
): string[] => {
    const dates = new Set<string>();
    const add = (value: unknown) => {
        const date = recordedDate(value);
        if (date?.startsWith(`${month}-`)) dates.add(formatDate(date));
    };
    rows.forEach((row) => row.eligibility.lineage.forEach((line) => add(line.member_cancelled_at)));
    terminations
        .filter((record) => record.hero_user_id === userId && record.termination_reason === 'membership_cancellation')
        .forEach((record) => add(record.terminated_at));
    return Array.from(dates);
};

export const terminationExplanation = (record: TerminationRecord, formatDate = enrollmentDate): string => {
    const reason =
        record.termination_reason === 'membership_cancellation'
            ? 'Membership cancelled'
            : record.termination_reason === 'membership_discharge'
              ? 'Membership discharged'
              : record.termination_reason === 'program_disenrollment'
                ? `Left ${record.program}`
                : 'Enrollment ended';
    return `${reason} on ${formatDate(record.terminated_at)}. No care codes for this program in this month.`;
};

export const terminationEvidenceMessage = (evidence: CareRows['termination_evidence']): string | null =>
    evidence.complete ? null :
        'Historical termination evidence is incomplete; missing records are unknown, not zero.';

export const decisionTokens = (row: CareRow): DecisionTokens => {
    if (
        !row.decision_basis_hash ||
        typeof row.decision_basis_schema_version !== 'number' ||
        !row.decision_rule_version
    ) {
        throw new Error('Refresh this code before making a decision.');
    }
    return {
        decision_state_event_id: row.decision_state_event_id,
        decision_basis_hash: row.decision_basis_hash,
        decision_basis_schema_version: row.decision_basis_schema_version,
        rule_version: row.decision_rule_version
    };
};

export const actionKey = (action: CodeActionDescriptor): string =>
    `${action.action}:${action.warning_kinds?.join(',') ?? ''}`;

// Use the service's current label. A stale old green row is never a payable result.
export const rowTone = (row: CareRow): string => {
    if (!row.is_current) return 'unknown';
    if (row.status_label === 'Billable') return 'billable';
    if (row.status_label === 'Needs review') return 'warning';
    if (row.status_label === 'Not billable' || row.status_label === 'No longer eligible')
        return 'not_billable';
    return 'unknown';
};

export type RowFilter = {
    currentStatus: string | null;
    code: string | null;
    billableOnly: boolean;
    changedOnly: boolean;
    missingOnly?: boolean;
};
export const filterRows = (rows: CareRow[], filter: RowFilter): CareRow[] => {
    const changedPatients = new Set(
        rows.filter((row) => row.changes.changed).map((row) => row.hero_user_id)
    );
    return rows.filter(
        (row) =>
            (!filter.currentStatus || row.status_label === filter.currentStatus) &&
            (!filter.code || row.code === filter.code) &&
            (!filter.billableOnly ||
                (row.is_current &&
                    row.current_payable_units !== null &&
                    row.current_payable_units > 0)) &&
            (!filter.changedOnly || changedPatients.has(row.hero_user_id)) &&
            (!filter.missingOnly ||
                (row.is_current &&
                    row.current_evidence.facts?.coverage?.matched_presence === false))
    );
};

export const reportLines = (report: CurrentReport | undefined) =>
    report?.available && report.by_code ? Object.entries(report.by_code) : undefined;

export const rowSeconds = (row: CareRow) => row.is_current ? row.actual_seconds : null;

export const codePresentation = (row: CareRow) => {
    const reportedOnly = row.candidate_source === 'csv_reported';
    const unitsValue = !row.is_current ? 'Unavailable' : row.current_payable_units == null
        ? 'Not decided' : String(row.current_payable_units);
    return {
        reportedOnly,
        seconds: !row.is_current ? null : reportedOnly
            ? row.current_evidence.facts?.raw_seconds ?? null
            : rowSeconds(row),
        status: !row.is_current ? 'Needs update' : row.status_label === 'On hold' ? 'Blocked' : row.status_label,
        timeBasis: !row.is_current ? 'Unavailable' : reportedOnly ? 'Reported' : 'Counted',
        unitsValue,
        units: unitsValue === 'Unavailable' ? 'Units unavailable' : unitsValue === 'Not decided'
            ? 'Units not decided'
            : `${unitsValue} ${unitsValue === '1' ? 'unit' : 'units'} to bill`
    };
};

export const rowNote = (row: CareRow): string =>
    row.note ??
    Array.from(
        new Set(
            (row.current_evidence.facts?.decision_events?.map((event) => event.note) ?? []).filter(
                (note): note is string => typeof note === 'string' && Boolean(note.trim())
            )
        )
    ).join('\n\n');

export const rowNotes = (row: CareRow): string[] => {
    const note = rowNote(row);
    return note.trim() ? [note] : [];
};

const REASON_EXPLANATIONS: Record<string, string> = {
    missing_from_minutes_csv:
        'This patient is missing from the uploaded minutes CSV. Import a corrected file, or explain why this code should not be billed.',
    csv_program_not_enrolled:
        'Care time was reported for this program, but no enrollment was found this month. Correct the enrollment, then sync patients and import again, or mark this code not billable.',
    termination_time_unverified:
        'Care was reported on a day when enrollment or membership ended. The CSV has no time of day, so these minutes are not counted. Check the recorded end date, or mark this code not billable; it cannot be approved while timing is unknown.',
    below_threshold: 'There is not enough care time to bill this code.',
    below_addon_threshold: 'There is not enough additional care time to bill this code.',
    base_not_billable:
        'This additional code cannot be billed because the first code is not billable.',
    base_needs_review: 'The first code needs a decision before this additional code can be billed.',
    operator_hold: 'Billing for this code is blocked. Unblock it to restore the calculated result.',
    operator_block: 'A reviewer decided not to bill this code.',
    block: 'Billing for this program is blocked.',
    hold: 'Billing for this program is blocked.',
    outside_eligibility: 'The recorded care took place outside the enrollment period.',
    unreleased_flag: 'An enrollment change needs a billing decision.',
    no_source_rows: 'No care activity was found in the imported report.',
    condition_not_met: 'A reviewer did not confirm the required conditions for this program.',
    condition_review_required: 'A reviewer must confirm the required conditions for this program.',
    rpm_concurrency_review_required:
        'RPM means Remote Patient Monitoring. Confirm RPM was not billed this month before approving this RTM code.',
    same_program_reenrollment_requires_review:
        'Enrollment changed this month. Check the enrollment history and this code’s care time before deciding whether to bill.',
    rtm_cross_org_month_conflict:
        'Another participating organization has already locked RTM billing for this patient this month.',
    no_longer_eligible: 'The patient is no longer enrolled for this billing period.',
    decision_state_invalid:
        'The saved billing decision could not be read. Ask support to check this code’s decision history.',
    episode_data_invalid_requires_review:
        'The program enrollment has missing or inconsistent information. Check its start/end dates and DrChrono patient link, then sync patients and calculate again.',
    member_dates_invalid_requires_review:
        'The recorded membership end is before its start. Correct the membership dates, then sync patients and calculate again.',
    open_episode_provider_mismatch_requires_review:
        'The enrollment and organization do not match. Check the enrollment record.',
    member_current_provider_unknown_requires_review:
        'Organization data is missing from the membership record. Sync patients to refresh it. If it is still missing, contact support before billing.',
};

const primaryReason = (row: CareRow): string | null => {
    const reason = row.operator_hold || row.result_reason || row.reason;
    return reason === 'unreleased_flag' && row.reason ? row.reason : reason;
};

export const remainingReviewKinds = (row: CareRow): string[] =>
    row.is_current && row.status_label === 'Needs review'
        ? Array.from(new Set(row.unresolved_warning_kinds ?? [])).filter((kind) => kind !== primaryReason(row))
        : [];

export const baseCodeFor = (row: CareRow, rowsById: ReadonlyMap<number, CareRow>): string | undefined => {
    const id = row.current_evidence.facts?.dependency?.base_charge_id;
    const base = id === null || id === undefined ? undefined : rowsById.get(id);
    return base && row.is_current && base.is_current &&
        base.hero_user_id === row.hero_user_id && base.program === row.program &&
        base.current_evidence.run_id === row.current_evidence.run_id
        ? base.code
        : undefined;
};

export const warningExplanation = (
    row: CareRow,
    reason: string,
    month?: string,
    baseCode?: string
): string => {
    if (reason === 'missing_from_minutes_csv')
        return `No ${row.program} minutes were matched for this patient in the uploaded CSV. Import a complete corrected file, or explain why this code should not be billed.`;
    if (reason === 'csv_program_not_enrolled') {
        const reported = row.current_evidence.facts?.raw_seconds;
        const time = reported == null ? '' : `${timeText(reported)} of `;
        return `${time}${row.program} care was reported, but no ${row.program} enrollment was found this month. Correct the enrollment, then sync patients and import again, or mark this code not billable.`;
    }
    if (reason === 'termination_time_unverified') {
        const activities = row.current_evidence.facts?.uncertain_activity ?? [];
        const dates = Array.from(new Set(activities.map((activity) => workDateText(activity.work_date))));
        return dates.length
            ? `Care was reported on ${dates.join('; ')}, when enrollment or membership ended. The CSV has no time of day, so these minutes are not counted. Check the end date, or mark this code not billable; it cannot be approved while timing is unknown.`
            : REASON_EXPLANATIONS.termination_time_unverified;
    }
    if (reason === 'rpm_concurrency_review_required') {
        const facts = row.current_evidence.facts;
        const rpm = facts?.coverage?.rpm_seconds;
        const rtm = facts?.raw_seconds ?? rowSeconds(row);
        if (rpm === null || rpm === undefined)
            return `An earlier import flagged RPM without retaining its minutes. RTM reported: ${timeText(rtm)}. Check the original CSV and import it again for an open month. ${REASON_EXPLANATIONS.rpm_concurrency_review_required}`;
        return `Reported this month: RPM ${timeText(rpm)}; RTM ${timeText(rtm)}. ${REASON_EXPLANATIONS.rpm_concurrency_review_required}`;
    }
    if (reason === 'member_dates_invalid_requires_review') {
        const conflicts = new Set<string>();
        row.eligibility.lineage.forEach((line) => {
            const start = recordedDate(line.member_activated_at);
            const endings = [recordedDate(line.member_cancelled_at), recordedDate(line.member_discharged_at)]
                .filter((date): date is string => date !== null).sort();
            const end = endings[0];
            if (start && end && end < start)
                conflicts.add(`Membership ended on ${enrollmentDate(end)}, before it started on ${enrollmentDate(start)}.`);
        });
        if (conflicts.size)
            return `${Array.from(conflicts).join(' ')} Correct the membership dates, then sync patients and calculate again.`;
    }
    if (reason === 'open_episode_provider_mismatch_requires_review')
        return `The patient’s current membership belongs to a different organization, but their ${row.program} enrollment here is still open. Confirm whether they transferred and correct the enrollment before billing.`;
    if (reason === 'episode_data_invalid_requires_review') {
        const problems = new Set<string>();
        row.eligibility.lineage.forEach((line) => {
            const start = recordedDate(line.program_enrollment_date);
            const end = recordedDate(line.program_disenrollment_date);
            if (start && end && end < start)
                problems.add(`${row.program} disenrollment on ${dateTime(end)} is before enrollment on ${dateTime(start)}.`);
            if (typeof line.drchrono_patient_id === 'string' && !line.drchrono_patient_id.trim())
                problems.add(`The ${row.program} enrollment has no linked DrChrono patient.`);
        });
        if (problems.size)
            return `${Array.from(problems).join(' ')} Correct the enrollment record, then sync patients and calculate again.`;
    }
    if (reason === 'base_needs_review')
        return baseCode
            ? `${row.code} depends on ${baseCode} being billable. Resolve ${baseCode} first.`
            : 'The first care code needs a billing decision before this additional code can be billed.';
    if (reason === 'base_not_billable')
        return baseCode
            ? `${row.code} cannot be billed because ${baseCode}, the required first code, is not billable.`
            : REASON_EXPLANATIONS.base_not_billable;
    if (reason === 'same_program_reenrollment_requires_review') {
        const periods = enrollmentPeriods(row);
        const changes = row.eligibility.windows.flatMap((window, index) => {
            const end = recordedDate(row.eligibility.windows[index - 1]?.eligible_end);
            const start = recordedDate(window.eligible_start);
            if (
                !end || !start || end >= start ||
                (month && (!end.startsWith(`${month}-`) || !start.startsWith(`${month}-`)))
            )
                return [];
            const programChanged =
                periods.some((period) => period.disenrolledAt === end) &&
                periods.some((period) => period.enrolledAt === start);
            return [
                programChanged
                    ? `left ${row.program} on ${enrollmentDate(end)} and re-enrolled on ${enrollmentDate(start)}`
                    : `had a break in ${row.program} care eligibility from ${enrollmentDate(end)} to ${enrollmentDate(start)}`
            ];
        });
        if (changes.length)
            return `The patient ${changes.join('; ')}. Review this code’s care time before deciding whether to bill.`;
        const memberIds = new Set(row.eligibility.lineage
            .map((line) => line.member_id).filter((id) => typeof id === 'number' && id > 0));
        if (memberIds.size > 1)
            return `More than one membership record is linked to ${row.program} this month. Check the membership dates and this code’s care time before deciding whether to bill.`;
    }
    return REASON_EXPLANATIONS[reason] ?? 'A reviewer needs to check this code before billing.';
};
export const rowExplanation = (row: CareRow, month?: string, baseCode?: string): string => {
    if (!row.is_current) return 'Billing results need updating. View evidence for details.';
    if (row.status_label === 'No longer eligible') return REASON_EXPLANATIONS.no_longer_eligible;
    if (row.status_label === 'Billable') {
        const actual = rowSeconds(row);
        return actual !== null && row.required_seconds !== null && actual >= row.required_seconds
            ? 'The monthly care time meets this code’s minimum.'
            : 'This code is approved for billing based on the recorded monthly result.';
    }

    const reason = primaryReason(row);
    if (reason === 'hold' || reason === 'operator_hold')
        return REASON_EXPLANATIONS.operator_hold;
    if (reason === 'below_threshold' || reason === 'below_addon_threshold') {
        const actual = rowSeconds(row);
        const required = row.required_seconds;
        if (actual !== null && required !== null) {
            const dropped = row.current_evidence.facts?.dropped_seconds ?? 0;
            const uncertain = (row.current_evidence.facts?.uncertain_activity ?? [])
                .reduce((total, activity) => total + activity.seconds, 0);
            const excluded = Math.max(0, dropped - uncertain);
            return `${timeText(actual)} counted this month; at least ${timeText(required)} needed for this code.${excluded > 0 ? ` ${timeText(excluded)} outside enrollment was not counted.` : ''}`;
        }
    }
    if (reason && REASON_EXPLANATIONS[reason]) return warningExplanation(row, reason, month, baseCode);
    if (row.status_label === 'Needs review')
        return 'A reviewer needs to decide whether this code can be billed.';
    if (row.status_label === 'Not billable')
        return 'This code cannot be billed. View evidence for details.';
    return 'No billing result yet. View evidence for details.';
};

// The row and overview are separate API reads. Never mix proof from different evaluated runs.
export const selectedEvidenceRow = (
    rows: CareRows | undefined,
    overview: CareMonthOverview | undefined,
    chargeId: number | undefined
) => {
    const row = rows?.rows.find((item) => item.charge_id === chargeId);
    if (
        !row ||
        !overview ||
        row.is_current !== overview.is_current ||
        row.current_evidence.run_id !== overview.run.evaluated_run_id
    )
        return undefined;
    return row;
};

export const resultContextsAgree = (
    rows: CareRows | undefined,
    overview: CareMonthOverview | undefined
): boolean =>
    Boolean(
        rows &&
        overview &&
        rows.rows.every(
            (row) =>
                row.is_current === overview.is_current &&
                row.current_evidence.run_id === overview.run.evaluated_run_id
        )
    );
